import { useEffect, useRef } from "react";
import { reportPlay } from "../library/playtime";
import { systemOf } from "../library/localLibrary";
import type { Emulator } from "./useEmulator";

/** No input for this long and time stops counting until the next one. */
export const PLAY_IDLE_MS = 30_000;
const REPORT_EVERY_MS = 60_000;

// Records time played for the signed-in account. A second counts when a game
// is running, the tab is visible, the game isn't paused, and there was input
// (keys, touch, mouse or a gamepad) in the last 30 seconds. Seconds are sent
// once a minute and whenever the tab is hidden or the game changes.
//
// The key listeners are capture-phase on window and added when the player
// mounts, ahead of EmulatorJS's, which stop key events from going further.
export function usePlaytime(emu: Emulator, signedIn: boolean) {
  const paused = useRef(emu.paused);
  useEffect(() => {
    paused.current = emu.paused;
  });
  const game = emu.current;
  // The cartridge's own console once known (a zip's name says nothing).
  const system = emu.engine === "ejs" ? (emu.ejsSystem ?? "n64") : (emu.cartridge ?? (game ? systemOf(game) : ""));

  useEffect(() => {
    if (!signedIn || !game) return;
    // Only time after the first input counts: loading isn't playing.
    let lastInput = 0;
    let pending = 0;
    const mark = () => {
      lastInput = Date.now();
    };
    const gamepadActive = () =>
      navigator
        .getGamepads?.()
        .some((pad) => pad && (pad.buttons.some((b) => b.pressed) || pad.axes.some((a) => Math.abs(a) > 0.3)));

    const flush = (keepalive = false) => {
      if (pending === 0) return;
      const seconds = pending;
      pending = 0;
      reportPlay(game, system, seconds, keepalive).catch(() => {
        // Keep it for the next report rather than losing the time.
        pending += seconds;
      });
    };

    const tick = window.setInterval(() => {
      if (gamepadActive()) mark();
      if (!document.hidden && !paused.current && Date.now() - lastInput < PLAY_IDLE_MS) pending += 1;
    }, 1000);
    const report = window.setInterval(() => flush(), REPORT_EVERY_MS);
    const onHide = () => document.hidden && flush(true);
    const onLeave = () => flush(true);

    for (const event of ["keydown", "pointerdown", "touchstart"]) window.addEventListener(event, mark, true);
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onLeave);
    return () => {
      window.clearInterval(tick);
      window.clearInterval(report);
      for (const event of ["keydown", "pointerdown", "touchstart"]) window.removeEventListener(event, mark, true);
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onLeave);
      flush(true);
    };
  }, [signedIn, game, system]);
}
